import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Share2, MessageSquare, Smartphone, Monitor, Chrome, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import SearchBar from '../components/SearchBar';
import ThemeToggle from '../components/ThemeToggle';
import apiService from '../services/api';
import { useBrandSettings } from '../context/BrandSettingsContext';
import BrandLogo from '../components/BrandLogo';

const SharePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logos } = useBrandSettings();
  const [sharedContent, setSharedContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasSharedContent, setHasSharedContent] = useState(false);

  useEffect(() => {
    // Extract shared content from URL parameters (from Share Target API or share links)
    const title = searchParams.get('title') || '';
    const text = searchParams.get('text') || '';
    const url = searchParams.get('url') || '';

    let content = '';
    if (text) {
      content = text;
    } else if (title) {
      content = title;
    }
    
    if (url && !content.includes(url)) {
      content = content ? `${content}\n\nSource: ${url}` : url;
    }

    if (content.trim()) {
      // If shared content received, redirect to homepage with claim pre-filled
      sessionStorage.setItem('prefill_claim', content.trim());
      navigate('/', { replace: true });
      return;
    }
    
    setHasSharedContent(false);
  }, [searchParams, navigate]);

  // Handle search from SearchBar
  const handleSearch = async (searchData) => {
    setIsLoading(true);
    try {
      const result = await apiService.factCheck(
        searchData.content,
        searchData.type,
        searchData.type === 'image' ? searchData.content : null,
        searchData.type === 'url' ? searchData.content : null
      );
      navigate('/results', { state: { result } });
    } catch (error) {
      console.error('Verification error:', error);
      navigate('/results', { state: { error: error.message || 'An error occurred' } });
    } finally {
      setIsLoading(false);
    }
  };

  // If content was shared (from extension), show the verification UI
  if (hasSharedContent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        {/* Header */}
        <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
          <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
              {logos?.default ? (
                <img src={logos.default} alt="KaiCheckIt" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl shadow-lg object-contain" />
              ) : (
                <BrandLogo size="responsive" data-testid="brand-logo-header" />
              )}
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
                KaiCheckIt
              </span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="max-w-2xl mx-auto">
            <Card className="border-2 border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/20">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <CardTitle className="text-lg text-slate-800 dark:text-slate-100">Ready to KaiCheckIt it</CardTitle>
                </div>
                <CardDescription className="dark:text-slate-400">
                  Review the content and click "Check" to verify
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SearchBar 
                  onSearch={handleSearch} 
                  isLoading={isLoading}
                  initialClaim={sharedContent} 
                  autoFocus={true} 
                />
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // Default: Show the Install & Share instructions page
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            {logos?.default ? (
              <img src={logos.default} alt="KaiCheckIt" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl shadow-lg object-contain" />
            ) : (
              <BrandLogo size="responsive" data-testid="brand-logo-header" />
            )}
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          
          {/* Page Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              How to Install & Share
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Get KaiCheckIt on your devices and verify content anywhere
            </p>
          </div>

          <div className="space-y-6">
            
            {/* Section 1: How to Install on Mobile - MOBILE FIRST */}
            <Card className="border-2 border-purple-200 dark:border-purple-800 bg-white dark:bg-slate-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-800 dark:text-slate-100">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-white" />
                  </div>
                  1. Install KaiCheckIt on Mobile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* iPhone */}
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    iPhone (Safari)
                  </h4>
                  <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-2 ml-1">
                    <li className="flex gap-2">
                      <span className="font-semibold text-purple-600 dark:text-purple-400 shrink-0">1.</span>
                      <span>Open KaiCheckIt in <strong>Safari</strong> browser</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-purple-600 dark:text-purple-400 shrink-0">2.</span>
                      <span>Tap the <strong>Share</strong> button (square with arrow)</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-purple-600 dark:text-purple-400 shrink-0">3.</span>
                      <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-purple-600 dark:text-purple-400 shrink-0">4.</span>
                      <span>Tap <strong>"Add"</strong></span>
                    </li>
                  </ol>
                </div>

                {/* Android */}
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    Android (Chrome)
                  </h4>
                  <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-2 ml-1">
                    <li className="flex gap-2">
                      <span className="font-semibold text-purple-600 dark:text-purple-400 shrink-0">1.</span>
                      <span>Open KaiCheckIt in <strong>Chrome</strong> browser</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-purple-600 dark:text-purple-400 shrink-0">2.</span>
                      <span>Tap the <strong>menu</strong> (three dots)</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-purple-600 dark:text-purple-400 shrink-0">3.</span>
                      <span>Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></span>
                    </li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Browser Extension (Desktop) */}
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-800 dark:text-slate-100">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                    <Chrome className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  2. Install Browser Extension (Desktop)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Add "Check with KaiCheckIt" to your right-click menu. Highlight any text and verify instantly!
                </p>

                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-3">
                    Chrome, Edge, or Brave
                  </h4>
                  <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-2 ml-1">
                    <li className="flex gap-2">
                      <span className="font-semibold text-blue-600 dark:text-blue-400 shrink-0">1.</span>
                      <span><a href="/kaicheckit-extension.zip" download="kaicheckit-extension.zip" className="text-blue-600 dark:text-blue-400 underline font-medium">Download the extension</a> (ZIP file)</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-blue-600 dark:text-blue-400 shrink-0">2.</span>
                      <span><strong>Extract/Unzip</strong> the ZIP file to a folder (e.g., Desktop or Documents)</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-blue-600 dark:text-blue-400 shrink-0">3.</span>
                      <span>Open Chrome and go to <code className="bg-slate-200 dark:bg-slate-600 px-1 rounded">chrome://extensions</code></span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-blue-600 dark:text-blue-400 shrink-0">4.</span>
                      <span>Enable <strong>"Developer mode"</strong> (toggle in top right corner)</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-blue-600 dark:text-blue-400 shrink-0">5.</span>
                      <span>Click <strong>"Load unpacked"</strong></span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-blue-600 dark:text-blue-400 shrink-0">6.</span>
                      <span>Navigate to and select the <strong>extracted folder</strong> containing manifest.json</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    <strong>✓ Once installed:</strong> Highlight any text → Right-click → <strong>"Check with KaiCheckIt"</strong>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Section 3: How to Verify Social Content */}
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-800 dark:text-slate-100">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                    <Share2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  3. How to Verify Social Content
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Desktop with extension */}
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                    <Monitor className="w-4 h-4" />
                    Desktop (with extension)
                  </h4>
                  <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-2 ml-1">
                    <li className="flex gap-2">
                      <span className="font-semibold text-green-600 dark:text-green-400 shrink-0">1.</span>
                      <span><strong>Highlight</strong> the suspicious text on any webpage</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-green-600 dark:text-green-400 shrink-0">2.</span>
                      <span><strong>Right-click</strong> and select <strong>"KaiCheckIt it"</strong></span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-green-600 dark:text-green-400 shrink-0">3.</span>
                      <span>Review and click <strong>"Check"</strong></span>
                    </li>
                  </ol>
                </div>

                {/* Mobile method */}
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    Mobile
                  </h4>
                  <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-2 ml-1">
                    <li className="flex gap-2">
                      <span className="font-semibold text-green-600 dark:text-green-400 shrink-0">1.</span>
                      <span><strong>Copy</strong> the suspicious text from social media</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-green-600 dark:text-green-400 shrink-0">2.</span>
                      <span><strong>Open</strong> KaiCheckIt app</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-green-600 dark:text-green-400 shrink-0">3.</span>
                      <span><strong>Paste</strong> and tap <strong>"Check"</strong></span>
                    </li>
                  </ol>
                </div>

                {/* Pro tip */}
                <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <p className="text-sm text-purple-800 dark:text-purple-200">
                    <strong>💡 Tip:</strong> Screenshot a post and use <strong>"Check Image"</strong> to analyze it directly!
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Section 4: How to Share KaiCheckIt Results */}
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-800 dark:text-slate-100">
                  <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/50 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  4. How to Share KaiCheckIt Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                  <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-2 ml-1">
                    <li className="flex gap-2">
                      <span className="font-semibold text-orange-600 dark:text-orange-400 shrink-0">1.</span>
                      <span>After verifying, click the <strong>"Share"</strong> button on the result</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-orange-600 dark:text-orange-400 shrink-0">2.</span>
                      <span>Choose where to share:</span>
                    </li>
                  </ol>
                  <ul className="text-sm text-slate-600 dark:text-slate-300 ml-8 mt-2 space-y-1">
                    <li>• <strong>Copy Link</strong> – Paste anywhere</li>
                    <li>• <strong>WhatsApp</strong> – Send to a chat</li>
                    <li>• <strong>X (Twitter)</strong> – Post or reply</li>
                    <li>• <strong>Facebook / LinkedIn</strong> – Share to your feed</li>
                  </ul>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>🎯 Stop misinformation:</strong> Reply to false claims with your KaiCheckIt result!
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <div className="text-center pt-4">
              <Button
                onClick={() => navigate('/')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              >
                Start Verifying
              </Button>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default SharePage;
